import { zodResolver } from "@hookform/resolvers/zod";
import { Check } from "@tamagui/lucide-icons";
import { BlurView } from "expo-blur";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { VerificationStatus } from "@/api/model";
import { useUpdateUserProfile } from "@/api/mutations/";
import type { UserProfileFormData } from "@/api/mutations/userProfile";
import { useGetAffiliationStatus, useGetUserProfile } from "@/api/queries/";
import {
  Button,
  Footer,
  H3,
  Header,
  Heading,
  ScrollView,
  Section,
  Separator,
  XStack,
  YStack,
  getTokenValue,
  useMedia,
} from "@/components";
import LoadingSpinner from "@/components/animated/LoadingSpinner";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { useZipcodeValidation } from "@/forms/hooks/useZipcodeValidation";
import {
  validateDate,
  validateMaxAge,
  validateMinAge,
  validateMmDdYyyyFormat,
} from "@/utils/validators";
import AffiliationBanner from "../profile/AffiliationBanner";
import { LocationInformation } from "../profile/LocationInformation";
import LockedProfile from "../profile/LockedProfile";
import ManageBilling from "../profile/ManageBilling";
import { PersonalInfo } from "../profile/PersonalInfo";

export const userProfileSchema = z
  .object({
    email: z.email("Email address is required"),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    preferredName: z.string().optional(),
    dateOfBirth: z
      .string()
      .optional()
      .refine((value) => !value || validateMmDdYyyyFormat(value), {
        message: "Please use mm/dd/yyyy with a 4-digit year",
      })
      .refine((value) => !value || validateDate(value), {
        message: "Invalid date",
      })
      .refine((value) => !value || validateMaxAge(value), {
        message: "Please enter a valid date of birth",
      })
      .refine((value) => !value || validateMinAge(value), {
        message: "You must be at least 18 years old",
      }),
    sexAssignedAtBirth: z.string().optional(),
    genderIdentity: z.string().optional(),
    pronouns: z.string().optional(),
    residingState: z.string().optional(),
    timezone: z.string().optional(),
    streetAddress: z.string().optional(),
    streetAddress2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zipCode: z
      .string()
      .optional()
      .refine((value) => !value || /^\d{5}$/.test(value), {
        message: "Zip code must be 5 digits",
      }),
  })
  .superRefine((data, ctx) => {
    const addressFields = [
      data.streetAddress,
      data.streetAddress2,
      data.city,
      data.state,
      data.zipCode,
    ];
    const anyAddressEntered = addressFields.some(
      (value) => value !== undefined && value.trim() !== "",
    );

    if (!anyAddressEntered) return;

    const requiredFields: Array<keyof typeof data> = [
      "streetAddress",
      "city",
      "state",
      "zipCode",
    ];

    requiredFields.forEach((field) => {
      const value = data[field];
      if (!value || value.trim() === "") {
        ctx.addIssue({
          code: "custom",
          path: [field],
          message: "Required",
        });
      }
    });
  });

export type UserProfileSchemaType = z.infer<typeof userProfileSchema>;

const EMPTY_PROFILE_VALUES: UserProfileSchemaType = {
  email: "",
  firstName: "",
  lastName: "",
  preferredName: "",
  dateOfBirth: "",
  sexAssignedAtBirth: "",
  genderIdentity: "",
  pronouns: "",
  residingState: "",
  timezone: "",
  streetAddress: "",
  streetAddress2: "",
  city: "",
  state: "",
  zipCode: "",
};

export default function SettingsIndexScreen() {
  const { lg } = useMedia();
  const { data } = useGetUserProfile();
  const serverFormData = data as UserProfileFormData;
  const { value: partnerAffiliationEnabled } = useFeatureFlag(
    "partner-affiliation",
    false,
  );
  const { data: affiliationStatus } = useGetAffiliationStatus(
    partnerAffiliationEnabled,
  );
  const membershipAffiliation = affiliationStatus?.membershipAffiliation;

  const { mutate: updateUserProfile, isPending: isUpdating } =
    useUpdateUserProfile();

  const profileValues = useMemo<UserProfileSchemaType>(() => {
    if (!serverFormData) return EMPTY_PROFILE_VALUES;

    return {
      ...EMPTY_PROFILE_VALUES,
      ...serverFormData,
      email: serverFormData.email ?? "",
      zipCode: serverFormData.zipCode?.slice(0, 5) || "",
    };
  }, [serverFormData]);

  const {
    clearErrors,
    control,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors, isDirty },
  } = useForm<UserProfileSchemaType>({
    resolver: zodResolver(userProfileSchema),
    mode: "onBlur",
    shouldFocusError: true,
    defaultValues: EMPTY_PROFILE_VALUES,
    values: profileValues,
  });

  const { zipcodeError, isValidatingZipcode } = useZipcodeValidation({
    control,
  });

  const handleSave = handleSubmit((formData) => {
    if (zipcodeError) {
      setFocus("zipCode");
      return;
    }
    if (isValidatingZipcode) return;
    updateUserProfile({ data: formData as UserProfileFormData });
  });

  const handleDiscard = () => {
    reset(profileValues, { keepFieldsRef: true });
    clearErrors();
  };

  const lockProfile =
    data?.idVerification?.status === VerificationStatus.approved;

  return (
    <Section flex={1} $lg={{ flex: 1, gap: "$250" }}>
      {lg && (
        <Header
          marginHorizontal="$screenPadding"
          marginTop="$screenPadding"
          gap="$250"
        >
          <H3>Profile</H3>
          <Separator borderColor="$fillNeutral" />
        </Header>
      )}
      <ScrollView
        contentContainerStyle={{
          margin: "$screenPadding",
          marginBottom: "$1000",
          ...(lg && {
            marginTop: "$50",
          }),
        }}
      >
        {lockProfile ? (
          <LockedProfile
            data={serverFormData}
            control={control}
            affiliationStatus={
              partnerAffiliationEnabled && membershipAffiliation
                ? membershipAffiliation
                : undefined
            }
          />
        ) : (
          <>
            {partnerAffiliationEnabled && membershipAffiliation && (
              <AffiliationBanner
                active={membershipAffiliation.active ?? null}
                partnerName={affiliationStatus.membershipPartnerName ?? null}
              />
            )}
            <YStack gap="$200">
              <Heading size="$sm">Personal Information</Heading>
              <PersonalInfo control={control} errors={errors} />
            </YStack>
          </>
        )}

        <Separator borderColor="$fillNeutral" marginVertical="$250" />

        <YStack gap="$200">
          <Heading size="$sm">Location Information</Heading>
          <LocationInformation
            control={control}
            errors={errors}
            zipcodeError={zipcodeError}
          />
        </YStack>

        <ManageBilling />
      </ScrollView>
      {isDirty && (
        <BlurView
          style={{
            position: "absolute",
            bottom: 0,
            width: "100%",
          }}
        >
          <Footer
            flexDirection="row"
            padding="$screenPadding"
            marginLeft="auto"
            gap="$50"
            alignSelf="flex-end"
          >
            <Button mode="tertiary" onPress={handleDiscard}>
              Discard
            </Button>
            <Button
              mode="primary"
              loading={isUpdating || isValidatingZipcode}
              icon={!isUpdating && !isValidatingZipcode && <Check />}
              onPress={handleSave}
            >
              {isUpdating && (
                <XStack marginRight="$150">
                  <LoadingSpinner size={getTokenValue("$300")} />
                </XStack>
              )}
              Save changes
            </Button>
          </Footer>
        </BlurView>
      )}
    </Section>
  );
}
